import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "@contexts/AuthContext";
import { oidcAuthClient, PopupBlockedError } from "@contexts/OidcAuthClient";
import { createDebugLogger } from "@utils/debugLog";
import type { SessionExpiredDialogViewModel } from "./SessionExpiredDialog.ViewModel.types";

const log = createDebugLogger("auth:session-expired");

/** How oidc-client-ts reports the user closing the popup: their choice, not a fault. */
const POPUP_CLOSED_BY_USER = "Popup closed by user";

export const useSessionExpiredDialogViewModel = (): SessionExpiredDialogViewModel => {
  const { isSessionExpired } = useAuth();
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  // State lags a double click by a render; the ref does not.
  const signingInRef = useRef(false);

  // A later expiry starts from a clean dialog, not the last one's outcome.
  useEffect(() => {
    if (!isSessionExpired) {
      setPopupBlocked(false);
      setHasFailed(false);
    }
  }, [isSessionExpired]);

  const handleSignIn = useCallback(() => {
    if (signingInRef.current) {
      return;
    }

    signingInRef.current = true;
    setIsSigningIn(true);
    setPopupBlocked(false);
    setHasFailed(false);

    // Called straight from the click, with nothing awaited first, so the
    // browser still treats the popup as user-initiated. The dialog closes when
    // the renewed session arrives through the auth context.
    oidcAuthClient
      .renewSessionInteractively()
      .catch((error: unknown) => {
        if (error instanceof PopupBlockedError) {
          setPopupBlocked(true);
          return;
        }

        if (error instanceof Error && error.message === POPUP_CLOSED_BY_USER) {
          return;
        }

        log("Interactive session renewal failed", error);
        setHasFailed(true);
      })
      .finally(() => {
        signingInRef.current = false;
        setIsSigningIn(false);
      });
  }, []);

  return {
    isOpen: isSessionExpired,
    isSigningIn,
    popupBlocked,
    hasFailed,
    handleSignIn,
  };
};
